import React, { useEffect, useState, useCallback } from 'react';
import {
  AppBar, Toolbar, Box, IconButton, Badge, Drawer,
  List, Divider, ListItemIcon, ListItemText, ListItemButton,
  useMediaQuery, useTheme, BottomNavigation, BottomNavigationAction,
  Paper, Tooltip, Avatar, Typography, Menu, MenuItem, Button
} from '@mui/material';
import {
  Settings, Notifications, Menu as MenuIcon, Home, People,
  Person, School, CalendarMonth, Assessment, Groups,
  ContentPaste, PersonAdd, Logout, HomeWork,
  Timeline, VolunteerActivism, Shield, AccountBalance
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTenant } from '../context/TenantContext';
import { useShell } from './ShellContext';
import api from '../axios';

const LIME = '#00F1A7';
const DARK = '#05080F';

function parseJwt(token) {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join('')));
  } catch { return null; }
}

const ROLE_LABELS = {
  admin: 'Administrador',
  professor: 'Professor',
  monitor: 'Monitor',
  aluno: 'Aluno',
  consolidador: 'Consolidador',
  secretaria: 'Secretaria',
  pastor: 'Pastor',
};

function Header({ onLogout }) {
  const tenant = useTenant();
  const token = localStorage.getItem('token');
  const payload = parseJwt(token);
  const tipo = payload?.tipo;
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [notifCount, setNotifCount] = useState(0);
  const [notifMenu, setNotifMenu] = useState(null);
  const [notifList, setNotifList] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [bottomNavValue, setBottomNavValue] = useState(location.pathname);

  useEffect(() => { setBottomNavValue(location.pathname); }, [location.pathname]);

  const carregarNotificacoes = useCallback(async () => {
    try {
      const res = await api.get('/notificacoes/count');
      setNotifCount(res.data.nao_lidas || 0);
    } catch {}
  }, []);

  useEffect(() => {
    if (!token) return;
    carregarNotificacoes();
    const interval = setInterval(carregarNotificacoes, 30000);
    return () => clearInterval(interval);
  }, [token, carregarNotificacoes]);

  const abrirNotificacoes = async (event) => {
    setNotifMenu(event.currentTarget);
    try {
      const res = await api.get('/notificacoes/todas', { params: { limit: 5 } });
      setNotifList(res.data?.notificacoes || []);
    } catch {}
  };

  const marcarLida = async (id) => {
    try {
      await api.patch(`/notificacoes/${id}/ler`);
      carregarNotificacoes();
      setNotifList(prev => prev.map(n => n.id === id ? {...n, lida: true} : n));
    } catch {}
  };

  const marcarTodasLidas = async () => {
    try {
      await api.patch('/notificacoes/ler-todas');
      setNotifCount(0);
      setNotifList(prev => prev.map(n => ({...n, lida: true})));
    } catch {}
  };

  const handleLogoClick = () => {
    if (!token) return navigate('/');
    if (tipo === 'consolidador') return navigate('/area-consolidador');
    if (tipo === 'aluno') return navigate('/area-aluno');
    if (tipo === 'professor' || tipo === 'monitor') return navigate('/area-startfy');
    return navigate('/home');
  };

  const handleLogout = () => {
    localStorage.clear();
    if (onLogout) onLogout();
    window.location.href = '/';
  };

  const getMenuItems = () => {
    if (!token) return [];
    if (tipo === 'admin' || tipo === 'admin_tenant') return [
      { text: 'Home', icon: <Home />, path: '/home' },
      { text: 'Start', icon: <School />, path: '/area-startfy' },
      { divider: true },
      { text: 'Alunos', icon: <People />, path: '/alunos' },
      { text: 'Turmas', icon: <Groups />, path: '/turmas' },
      { text: 'Professores', icon: <School />, path: '/professores' },
      { text: 'Períodos', icon: <CalendarMonth />, path: '/periodos' },
      { text: 'Aulas', icon: <ContentPaste />, path: '/aulas' },
      { text: 'Matrículas', icon: <PersonAdd />, path: '/matriculas' },
      { text: 'Frequências', icon: <Assessment />, path: '/frequencias' },
      { text: 'Calendário', icon: <CalendarMonth />, path: '/calendario-aulas' },
      { text: 'Formandos', icon: <School />, path: '/formandos' },
      { divider: true },
      { text: 'Consolidadores', icon: <Groups />, path: '/gestao-consolidadores' },
      { text: 'Visitantes', icon: <People />, path: '/gestao-visitantes' },
      { divider: true },
      { text: 'Relatórios', icon: <Assessment />, path: '/relatorios' },
      { text: 'Dashboard', icon: <Assessment />, path: '/dashboard' },
      { divider: true },
      { text: 'Notificações', icon: <Notifications />, path: '/notificacoes', badge: notifCount },
      { text: 'Usuários', icon: <Person />, path: '/usuarios' },
    ];
    if (tipo === 'professor' || tipo === 'monitor') return [
      { text: 'Área do Professor', icon: <Home />, path: '/area-startfy' },
      { text: 'Calendário', icon: <CalendarMonth />, path: '/calendario-aulas' },
    ];
    if (tipo === 'aluno') return [
      { text: 'Minha Área', icon: <Home />, path: '/area-aluno' },
    ];
    if (tipo === 'consolidador') return [
      { text: 'Área do Consolidador', icon: <Home />, path: '/area-consolidador' },
      { text: 'Capturar Card', icon: <ContentPaste />, path: '/capturar-card' },
      { text: 'Notificações', icon: <Notifications />, path: '/notificacoes', badge: notifCount },
    ];
    if (tipo === 'secretaria' || tipo === 'pastor') return [
      { text: 'Secretaria', icon: <HomeWork />, path: '/secretaria' },
      { text: 'Cursos', icon: <School />, path: '/cursos' },
      { text: 'Financeiro', icon: <AccountBalance />, path: '/financeiro' },
    ];
    return [
      { text: 'Home', icon: <Home />, path: '/home' },
      { text: 'Módulos', icon: <HomeWork />, path: '/home' },
    ];
  };

  const getModuleContext = () => {
    const p = location.pathname;
    if (p === '/home' || p === '/') return 'home';
    if (p.startsWith('/area-startfy') || p.startsWith('/alunos') || p.startsWith('/turmas') ||
        p.startsWith('/professores') || p.startsWith('/periodos') || p.startsWith('/aulas') ||
        p.startsWith('/matriculas') || p.startsWith('/frequencias') || p.startsWith('/calendario') ||
        p.startsWith('/formandos')) return 'start';
    if (p.startsWith('/secretaria') || p.startsWith('/membros') || p.startsWith('/migracao')) return 'secretaria';
    if (p.startsWith('/cursos') || p.startsWith('/cursos-admin')) return 'cursos';
    if (p.startsWith('/gestao-visitantes') || p.startsWith('/gestao-consolidadores') ||
        p.startsWith('/area-consolidador') || p.startsWith('/capturar-card')) return 'consolidacao';
    if (p.startsWith('/discipulado')) return 'discipulado';
    if (p.startsWith('/grupos-crescimento')) return 'gcs';
    if (p.startsWith('/jornada')) return 'jornada';
    if (p.startsWith('/notificacao')) return 'notificacoes';
    if (p.startsWith('/voluntariado')) return 'voluntariado';
    if (p.startsWith('/financeiro')) return 'financeiro';
    if (p.startsWith('/relatorios') || p.startsWith('/dashboard') || p.startsWith('/admin')) return 'admin';
    return 'home';
  };

  const getBottomNavItems = () => {
    if (!token) return [];
    const ctx = getModuleContext();

    if (tipo === 'aluno') return [
      { label: 'Minha Área', value: '/area-aluno', icon: <Home /> },
    ];

    if (tipo === 'professor' || tipo === 'monitor') return [
      { label: 'Área', value: '/area-startfy', icon: <Home /> },
      { label: 'Calendário', value: '/calendario-aulas', icon: <CalendarMonth /> },
    ];

    if (tipo === 'consolidador') return [
      { label: 'Área', value: '/area-consolidador', icon: <Home /> },
      { label: 'Capturar', value: '/capturar-card', icon: <ContentPaste /> },
      { label: 'Notif.', value: '/notificacoes', icon: <Badge badgeContent={notifCount} color="error"><Notifications /></Badge> },
    ];

    switch (ctx) {
      case 'start':
        return [
          { label: 'Home', value: '/home', icon: <Home /> },
          { label: 'Alunos', value: '/alunos', icon: <People /> },
          { label: 'Frequências', value: '/frequencias', icon: <Assessment /> },
          { label: 'Calendário', value: '/calendario-aulas', icon: <CalendarMonth /> },
        ];
      case 'secretaria':
        return [
          { label: 'Home', value: '/home', icon: <Home /> },
          { label: 'Secretaria', value: '/secretaria', icon: <HomeWork /> },
          { label: 'Migração', value: '/migracao-alunos', icon: <PersonAdd /> },
        ];
      case 'cursos':
        return [
          { label: 'Home', value: '/home', icon: <Home /> },
          { label: 'Cursos', value: '/cursos', icon: <School /> },
        ];
      case 'consolidacao':
        return [
          { label: 'Home', value: '/home', icon: <Home /> },
          { label: 'Visitantes', value: '/gestao-visitantes', icon: <People /> },
          { label: 'Consolid.', value: '/gestao-consolidadores', icon: <Groups /> },
        ];
      case 'discipulado':
        return [
          { label: 'Home', value: '/home', icon: <Home /> },
          { label: 'Discipulados', value: '/discipulados', icon: <People /> },
        ];
      case 'gcs':
        return [
          { label: 'Home', value: '/home', icon: <Home /> },
          { label: 'GCs', value: '/grupos-crescimento', icon: <Groups /> },
        ];
      case 'jornada':
        return [
          { label: 'Home', value: '/home', icon: <Home /> },
          { label: 'Jornada', value: '/jornada', icon: <Timeline /> },
        ];
      case 'voluntariado':
        return [
          { label: 'Home', value: '/home', icon: <Home /> },
          { label: 'Voluntários', value: '/voluntariado', icon: <VolunteerActivism /> },
        ];
      case 'financeiro':
        return [
          { label: 'Home', value: '/home', icon: <Home /> },
          { label: 'Financeiro', value: '/financeiro', icon: <AccountBalance /> },
        ];
      case 'notificacoes':
        return [
          { label: 'Home', value: '/home', icon: <Home /> },
          { label: 'Notificações', value: '/notificacoes-centro', icon: <Notifications /> },
        ];
      default:
        return [
          { label: 'Home', value: '/home', icon: <Home /> },
          { label: 'Start', value: '/area-startfy', icon: <School /> },
          { label: 'Notif.', value: '/notificacoes', icon: <Badge badgeContent={notifCount} color="error"><Notifications /></Badge> },
        ];
    }
  };

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) return;
    setDrawerOpen(open);
  };

  const inShell = useShell();
  if (inShell) return null;

  return (
    <>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: DARK, borderBottom: '1px solid #2a2a2a' }}>
        <Toolbar sx={{
          maxWidth: 1200, mx: 'auto', width: '100%',
          px: { xs: 2, md: 4 },
          display: 'flex', justifyContent: 'space-between',
          minHeight: { xs: 56, md: 64 },
        }}>
          {/* Hamburger (mobile) */}
          {isMobile && token && (
            <IconButton edge="start" color="inherit" aria-label="menu" onClick={toggleDrawer(true)} sx={{ mr: 0.5 }}>
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={handleLogoClick}>
            <Box
              component="img"
              src={process.env.PUBLIC_URL + '/images/logo_conecta_church_white_v2.svg'}
              alt="ConectaChurch Logo"
              sx={{ height: 40, mr: 2 }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            {tenant && (
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', ml: 1, display: { xs: 'none', md: 'block' } }}>
                {tenant.nome}
              </Typography>
            )}
          </Box>

          {/* Desktop: actions */}
          {token && !isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {/* Admin module name based on context */}
              <Typography
                sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', mr: 1, fontWeight: 500, letterSpacing: 0.5 }}
              >
                {tipo === 'admin' || tipo === 'admin_tenant' ? 'ADMIN' : (ROLE_LABELS[tipo] || tipo).toUpperCase()}
              </Typography>

              {/* Notificações */}
              {(tipo === 'admin' || tipo === 'admin_tenant' || tipo === 'secretaria' || tipo === 'pastor' || tipo === 'consolidador') && (
                <Tooltip title="Notificações">
                  <IconButton onClick={abrirNotificacoes} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}>
                    <Badge badgeContent={notifCount} color="error">
                      <Notifications fontSize="small" />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}

              {/* Settings admin */}
              {(tipo === 'admin' || tipo === 'admin_tenant') && (
                <Tooltip title="Administração">
                  <IconButton onClick={() => navigate('/admin-dashboard')} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}>
                    <Settings fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {/* Episcopal Dashboard (super_admin only) */}
              {tipo === 'super_admin' && (
                <Tooltip title="Painel Episcopal">
                  <IconButton onClick={() => navigate('/episcopal')} sx={{ color: LIME, '&:hover': { bgcolor: 'rgba(171,219,37,0.1)' } }}>
                    <Shield fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {/* Avatar + sair */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5, pl: 1.5, borderLeft: '1px solid rgba(255,255,255,0.1)' }}>
                <Avatar sx={{ width: 30, height: 30, bgcolor: LIME, color: DARK, fontSize: 12, fontWeight: 800 }}>
                  {tipo ? tipo[0].toUpperCase() : '?'}
                </Avatar>
                <Tooltip title="Sair">
                  <IconButton onClick={handleLogout} size="small" sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff' } }}>
                    <Logout fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Menu de notificações */}
      <Menu
        anchorEl={notifMenu}
        open={Boolean(notifMenu)}
        onClose={() => setNotifMenu(null)}
        PaperProps={{ sx: { width: 360, maxHeight: 480 } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Notificações</Typography>
          <Button size="small" onClick={marcarTodasLidas} sx={{ fontSize: 12 }}>Marcar todas lidas</Button>
        </Box>
        <Divider />
        {notifList.length === 0 ? (
          <MenuItem disabled><ListItemText primary="Nenhuma notificação" /></MenuItem>
        ) : (
          notifList.map(n => (
            <MenuItem
              key={n.id}
              onClick={() => { marcarLida(n.id); if (n.link) window.location.href = n.link; }}
              sx={{ bgcolor: n.lida ? 'transparent' : 'action.hover' }}
            >
              <ListItemText
                primary={n.mensagem}
                secondary={new Date(n.data_criacao).toLocaleString('pt-BR')}
                primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: n.lida ? 400 : 600 } }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </MenuItem>
          ))
        )}
        <Divider />
        <MenuItem onClick={() => { setNotifMenu(null); window.location.href = '/notificacoes-centro'; }}>
          <ListItemText primary="Ver todas" primaryTypographyProps={{ align: 'center', variant: 'body2', sx: { fontWeight: 700 } }} />
        </MenuItem>
      </Menu>

      {/* Drawer (mobile) */}
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box sx={{ width: 272 }} role="presentation" onClick={toggleDrawer(false)} onKeyDown={toggleDrawer(false)}>
          {/* Cabeçalho drawer */}
          <Box sx={{ bgcolor: DARK, color: '#fff', p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: LIME, color: DARK, fontWeight: 800 }}>
              {tipo ? tipo[0].toUpperCase() : '?'}
            </Avatar>
            <Box>
              <Typography variant="body1" fontWeight={700} color="#fff">ConectaChurch</Typography>
              <Typography variant="caption" sx={{ color: '#aaa' }}>{ROLE_LABELS[tipo] || tipo}</Typography>
            </Box>
          </Box>

          <List sx={{ pt: 1 }}>
            {getMenuItems().map((item, index) =>
              item.divider ? (
                <Divider key={`d-${index}`} sx={{ my: 0.5 }} />
              ) : (
                <ListItemButton key={index} onClick={() => navigate(item.path)}
                  selected={location.pathname === item.path}
                  sx={{ borderRadius: 1, mx: 1, mb: 0.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: location.pathname === item.path ? LIME : 'text.secondary' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: location.pathname === item.path ? 700 : 400 }} />
                  {item.badge > 0 && <Badge badgeContent={item.badge} color="error" sx={{ ml: 1 }} />}
                </ListItemButton>
              )
            )}
          </List>

          <Divider />
          <List>
            <ListItemButton onClick={handleLogout} sx={{ borderRadius: 1, mx: 1, color: '#c00' }}>
              <ListItemIcon sx={{ minWidth: 36, color: '#c00' }}><Logout /></ListItemIcon>
              <ListItemText primary="Sair" />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>

      {/* Bottom navigation (mobile) */}
      {isMobile && token && getBottomNavItems().length > 0 && (
        <>
          <Box sx={{ height: 56, flexShrink: 0 }} />
          <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100, borderTop: `2px solid ${LIME}` }} elevation={4}>
          <BottomNavigation
            value={bottomNavValue}
            onChange={(_, newValue) => { setBottomNavValue(newValue); navigate(newValue); }}
            showLabels
            sx={{ '& .Mui-selected': { color: DARK }, '& .MuiBottomNavigationAction-root': { minWidth: 60 } }}
          >
            {getBottomNavItems().map((item, index) => (
              <BottomNavigationAction key={index} label={item.label} value={item.value} icon={item.icon} />
            ))}
          </BottomNavigation>
        </Paper>
        </>
      )}
    </>
  );
}

export default Header;
